// 文档验证相关的工具函数

/**
 * Check whether a document status value represents "done" (uploaded or verified).
 * Handles identification_proof array format: ["frontStatus", "backStatus"].
 */
const isStatusDone = status => {
  if (Array.isArray(status)) {
    return status.every(s => s === 'unverified' || s === 'verified');
  }
  return status === 'unverified' || status === 'verified';
};

/**
 * Check whether any side of a document status is "done".
 */
const isAnyStatusDone = status => {
  if (Array.isArray(status)) {
    return status.some(s => s === 'unverified' || s === 'verified');
  }
  return status === 'unverified' || status === 'verified';
};

/**
 * 判断单条 summary 状态是否算"已完成"（待审核或审核通过）。
 */
const isSummaryDone = status => status === 'unverified' || status === 'verified';

/**
 * 根据 identification_proof_summary 判断 Proof of identity 是否已有一套完整提交。
 * 用户只需完成一种证件类型即算达标：
 * - passport：单面完成
 * - national_id / driving_license：正反两面都完成
 */
const isProofSummaryDone = summary => {
  return (
    isSummaryDone(summary.passport) ||
    (isSummaryDone(summary.national_id_front) && isSummaryDone(summary.national_id_back)) ||
    (isSummaryDone(summary.driving_license_front) && isSummaryDone(summary.driving_license_back))
  );
};

/**
 * 根据 identification_proof_summary 判断是否已有任何一部分提交（部分进度）。
 */
const isProofSummaryAnyDone = summary => {
  return [
    summary.passport,
    summary.national_id_front,
    summary.national_id_back,
    summary.driving_license_front,
    summary.driving_license_back,
  ].some(isSummaryDone);
};

/**
 * 判断所有必要文档类型为可用并且所有类型文件待审核或审核通过
 * @param {Object} documents - 文档状态对象，包含各文档的状态信息
 * @param {Object} kycSettings - KYC设置对象，包含各文档类型的配置信息
 * @returns {string} - submitted | temporary_saved | not_saved
 */
export const areAllDocumentsValid = (documents, kycSettings) => {
  // Proof of identity 的状态以 summary（按类型/正反面聚合）为准
  const proofSummary = documents.identificationProofSummary || {};

  // 定义文档类型数组
  const documentTypes = [
    { config: kycSettings.passport, status: documents.passportStatus, name: 'passport' },
    { config: kycSettings.passport_with_face, status: documents.passportWithFaceStatus, name: 'passportWithFace' },
    { config: kycSettings.address_proof, status: documents.addressProofStatus, name: 'addressProof' },
    {
      config: kycSettings.identification_proof,
      name: 'identificationProof',
      // Proof of identity 不再用 identificationProofStatus（它依赖 user.poi_doc_type，
      // 可能和实际已上传的文档不一致），改用 identificationProofSummary 判定
      isDone: () => isProofSummaryDone(proofSummary),
      isAnyDone: () => isProofSummaryAnyDone(proofSummary),
    },
    { config: kycSettings.additional_document, status: documents.additionalDocumentStatus, name: 'additionalDocument' },
  ];

  // 过滤出可用的文档类型
  // 如果在白名单内，则地址证明不作为KYC阶段的必需文档
  const enabledDocuments = documentTypes.filter(
    doc => doc.config && doc.config.enable && !(doc.name === 'addressProof' && documents.isInWhiteList)
  );

  // 如果没有可用的文档类型，则返回false
  if (enabledDocuments.length === 0) return 'not_saved';

  // 单个文档是否"完成"：有自定义判定的用自定义判定，否则按 status 判定
  const isDocDone = doc => (doc.isDone ? doc.isDone() : isStatusDone(doc.status));
  const isDocAnyDone = doc => (doc.isAnyDone ? doc.isAnyDone() : isAnyStatusDone(doc.status));

  // 检查所有可用文档是否全部完成（待审核或审核通过）
  if (enabledDocuments.every(isDocDone)) return 'submitted';

  // 检查是否有部分文档完成
  if (enabledDocuments.some(isDocAnyDone)) return 'temporary_saved';

  return 'not_saved';
};
